import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useAuth0 } from '@auth0/auth0-react';

// Actions
import * as PiCoreActions from 'Shared/actions/PiCore.actions';

// Hooks
import { useOrganization } from './use-organization';
import { useToggle } from 'Shared/utils/hooks';

// Selectors
import { getPartnerInfo } from 'Shared/selectors/PartnerInfo.selectors';

// Types
import { PartnerInfo } from 'Shared/types/PartnerInfo.type';

export const usePartnerInfo = () => {
  const dispatch = useDispatch();

  const [isFetching, toggleIsFetching] = useToggle(false);
  const { organization, isPiOrganization } = useOrganization();
  const [hasToken, setHasToken] = useState(false);

  const partnerInfo: PartnerInfo = useSelector(state => getPartnerInfo(state));
  const { getAccessTokenSilently } = useAuth0();

  const auth0Ready = useCallback(async () => {
    const jwt = await getAccessTokenSilently();
    if (jwt) {
      setHasToken(true);
    }
  }, [getAccessTokenSilently]);

  // Ensure the user is logged in and we have a token before we can make a call to this API
  useEffect(() => {
    auth0Ready();
  }, []);

  useEffect(() => {
    if (!hasToken) {
      return;
    }

    if (!isPiOrganization || !isEmpty(partnerInfo)) return;
    toggleIsFetching();
    dispatch(
      PiCoreActions.getPartner({
        companyGuid: organization.pi_company_guid,
      })
    ).then(() => toggleIsFetching());
  }, [hasToken]);

  return {
    isFetching,
    partnerInfo,
  };
};
